// src/components/configuration/sections/team/TeamMemberEditor.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  User as UserIcon,
  Mail,
  Activity,
  Trash2,
  Save,
  AlertTriangle,
  Lock,
  Check,
  X,
  Clock,
  Calendar,
  Settings,
  Users as UsersIcon,
  RefreshCw,
  Send
} from 'lucide-react';

// shadcn components
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Separator } from '../../../ui/separator';

import { cn } from '../../../../utils/cn';

// ================================
// CANONICAL PATTERN: Initial forms + derive functions
// ================================

const initialBasicForm = { name: '', email: '' };
const initialRoleForm = { role: 'staff' };

const deriveBasicForm = (member) => ({
  name: member?.user?.name || '',
  email: member?.user?.email || '',
});

const deriveRoleForm = (member) => ({
  role: member?.role || 'staff',
});

// ================================
// SHARED HELPERS (used by both member + invitation views)
// ================================

const getRoleConfig = (role) => {
  switch (role) {
    case 'admin':
      return { label: 'Administrator' };
    case 'staff':
      return { label: 'Staff' };
    default:
      return { label: 'Unknown' };
  }
};

const getRolePermissions = (role) => {
  const map = {
    admin: { manage_team: true, add_team_members: true, update_team_roles: true, manage_services: true, manage_customers: true, manage_events: true, view_all_events: true, manage_invoices: true, view_financials: true },
    staff: { manage_team: false, add_team_members: false, update_team_roles: false, manage_services: false, manage_customers: true, manage_events: true, view_all_events: false, manage_invoices: true, view_financials: false }
  };
  return map[role] || map.staff;
};

const permissionCategories = [
  { id: 'team', title: 'Team Management', icon: UsersIcon, permissions: [{ key: 'manage_team', label: 'Manage Team' }, { key: 'add_team_members', label: 'Invite Members' }, { key: 'update_team_roles', label: 'Update Roles' }] },
  { id: 'business', title: 'Business', icon: Settings, permissions: [{ key: 'manage_services', label: 'Services' }] },
  { id: 'customers', title: 'Customers', icon: UsersIcon, permissions: [{ key: 'manage_customers', label: 'Manage Customers' }] },
  { id: 'events', title: 'Events', icon: Calendar, permissions: [{ key: 'manage_events', label: 'Manage Events' }, { key: 'view_all_events', label: 'View All Events' }] },
  { id: 'financial', title: 'Financial', icon: Settings, permissions: [{ key: 'manage_invoices', label: 'Manage Invoices' }, { key: 'view_financials', label: 'View Financials' }] }
];

const roleOptions = [
  { value: 'staff', label: 'Staff Member', description: 'Can manage customers, appointments, and invoices. Cannot access team settings or financial reports.' },
  { value: 'admin', label: 'Administrator', description: 'Full access including team management, business settings, and financial reports. Can invite and manage team members.' }
];

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  else if (diffDays === 1) return 'Yesterday';
  else if (diffDays < 7) return `${diffDays} days ago`;
  else return date.toLocaleDateString();
};

const getTimeSinceJoined = (createdAt) => {
  if (!createdAt) return 'Unknown';
  const joinDate = new Date(createdAt);
  const now = new Date();
  const diffMonths = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24 * 30));
  if (diffMonths < 1) {
    const diffDays = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
    return diffDays === 0 ? 'Today' : `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  else {
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
  }
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getOnlineStatus = (lastSeen) => {
  if (!lastSeen) return { color: 'text-slate-500', bgColor: 'bg-slate-500', label: 'Offline' };
  const diffMinutes = (new Date() - new Date(lastSeen)) / (1000 * 60);
  if (diffMinutes < 5) return { color: 'text-emerald-400', bgColor: 'bg-emerald-400', label: 'Online' };
  else if (diffMinutes < 30) return { color: 'text-amber-400', bgColor: 'bg-amber-400', label: 'Away' };
  else return { color: 'text-slate-500', bgColor: 'bg-slate-500', label: 'Offline' };
};

// ================================
// COMPONENT
// ================================

const TeamMemberEditor = ({
  item,
  currentUser,
  onUpdateMember,
  onUpdateRole,
  onRemoveMember,
  onReactivateMember,
  onCancelInvitation,
  onResendInvitation,
  permissions = {},
  savingMember = false,
  savingRole = false
}) => {
  const itemType = item?.type;
  const data = item?.data;
  const isInvitation = itemType === 'invitation';
  const isMember = itemType === 'member';
  const member = isMember ? data : null;
  const invitation = isInvitation ? data : null;

  // ================================
  // CANONICAL: Local form state
  // ================================
  const [basicForm, setBasicForm] = useState(initialBasicForm);
  const [roleForm, setRoleForm] = useState(initialRoleForm);

  // ================================
  // CANONICAL: Baselines for dirty tracking
  // ================================
  const [basicBaseline, setBasicBaseline] = useState(initialBasicForm);
  const [roleBaseline, setRoleBaseline] = useState(initialRoleForm);

  // ================================
  // CANONICAL: Member switch detection
  // ================================
  const prevMemberId = useRef(null);

  // ================================
  // CANONICAL: Saving flag watchers
  // ================================
  const prevSavingMember = useRef(false);
  const prevSavingRole = useRef(false);

  // UI state
  const [activeSection, setActiveSection] = useState('basic');
  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Section refs for scroll-to
  const sectionRefs = {
    basic: useRef(null),
    role: useRef(null),
    permissions: useRef(null),
    activity: useRef(null)
  };
  const sectionPositions = useRef({});

  // ================================
  // CANONICAL: Initialization — member switch vs server update
  // ================================
  useEffect(() => {
    if (!member) return;

    const isMemberSwitch = prevMemberId.current !== member.id;
    prevMemberId.current = member.id;

    if (isMemberSwitch) {
      // Always reset on member switch
      const newBasic = deriveBasicForm(member);
      const newRole = deriveRoleForm(member);
      setBasicForm(newBasic);
      setBasicBaseline(newBasic);
      setRoleForm(newRole);
      setRoleBaseline(newRole);
      setErrors({});
      setActiveSection('basic');
    } else {
      // Server update to same member — guard with hasChanges
      const currentBasicHasChanges = JSON.stringify(basicForm) !== JSON.stringify(basicBaseline);
      if (!currentBasicHasChanges) {
        const newBasic = deriveBasicForm(member);
        setBasicForm(newBasic);
        setBasicBaseline(newBasic);
      }

      const currentRoleHasChanges = JSON.stringify(roleForm) !== JSON.stringify(roleBaseline);
      if (!currentRoleHasChanges) {
        const newRole = deriveRoleForm(member);
        setRoleForm(newRole);
        setRoleBaseline(newRole);
      }
    }
  }, [member?.id, member?.user?.name, member?.user?.email, member?.role]);

  // ================================
  // CANONICAL: Saving flag watcher — Basic Info
  // ================================
  useEffect(() => {
    if (prevSavingMember.current && !savingMember && member) {
      const newBasic = deriveBasicForm(member);
      setBasicBaseline(newBasic);
      setBasicForm(newBasic);
    }
    prevSavingMember.current = savingMember;
  }, [savingMember, member]);

  // ================================
  // CANONICAL: Saving flag watcher — Role
  // ================================
  useEffect(() => {
    if (prevSavingRole.current && !savingRole && member) {
      const newRole = deriveRoleForm(member);
      setRoleBaseline(newRole);
      setRoleForm(newRole);
    }
    prevSavingRole.current = savingRole;
  }, [savingRole, member]);

  // ================================
  // CANONICAL: hasChanges
  // ================================
  const hasBasicChanges = JSON.stringify(basicForm) !== JSON.stringify(basicBaseline);
  const hasRoleChanges = JSON.stringify(roleForm) !== JSON.stringify(roleBaseline);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Calculate section positions
  useEffect(() => {
    if (!member) return;
    const calculatePositions = () => {
      const contentDiv = sectionRefs.basic?.current?.parentElement;
      if (!contentDiv) return;
      const containerTop = contentDiv.scrollTop;
      const positions = {};
      Object.entries(sectionRefs).forEach(([id, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const parentRect = contentDiv.getBoundingClientRect();
          positions[id] = rect.top - parentRect.top + containerTop;
        }
      });
      sectionPositions.current = positions;
    };
    setTimeout(calculatePositions, 100);
  }, [member]);

  // Scroll spy
  useEffect(() => {
    if (!member) return;
    const contentDiv = sectionRefs.basic?.current?.parentElement;
    if (!contentDiv) return;

    const handleScroll = () => {
      const scrollTop = contentDiv.scrollTop;
      const scrollHeight = contentDiv.scrollHeight;
      const clientHeight = contentDiv.clientHeight;
      const positions = sectionPositions.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setActiveSection(prev => prev !== 'activity' ? 'activity' : prev);
        return;
      }

      const sectionOrder = ['activity', 'permissions', 'role', 'basic'];
      for (const sectionId of sectionOrder) {
        const sectionTop = positions[sectionId];
        if (sectionTop !== undefined && scrollTop >= sectionTop - 100) {
          setActiveSection(prev => prev !== sectionId ? sectionId : prev);
          break;
        }
      }
    };

    contentDiv.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => contentDiv.removeEventListener('scroll', handleScroll);
  }, [member]);

  // ================================
  // CANONICAL: Diff-only submit handlers
  // ================================
  const handleSaveBasic = () => {
    const newErrors = {};
    if (!basicForm.name.trim()) newErrors.name = 'Name is required';
    if (!basicForm.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(basicForm.email)) newErrors.email = 'Invalid email';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Diff-only: only send changed fields
    const changedFields = {};
    if (basicForm.name !== basicBaseline.name) changedFields.name = basicForm.name;
    if (basicForm.email !== basicBaseline.email) changedFields.email = basicForm.email;

    if (Object.keys(changedFields).length > 0) {
      onUpdateMember(member.id, changedFields);
    }
    setErrors({});
  };

  const handleSaveRole = () => {
    if (roleForm.role !== roleBaseline.role) {
      onUpdateRole(member.id, roleForm.role);
    }
  };

  // ================================
  // EMPTY STATE
  // ================================
  if (!item || !data) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center p-8">
          <UserIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Select a Team Member or Invitation</h3>
          <p className="text-muted-foreground text-sm">
            Choose a member or pending invitation from the list to view and manage their details
          </p>
        </div>
      </div>
    );
  }

  // ================================
  // INVITATION VIEW (read-only + action buttons — no form pattern)
  // ================================
  if (isInvitation && invitation) {
    const roleConfig = getRoleConfig(invitation.role);
    const invitationPermissions = getRolePermissions(invitation.role);

    const getDaysRemaining = (expiresAt) => {
      if (!expiresAt) return null;
      const now = new Date();
      const expiry = new Date(expiresAt);
      return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    };

    const daysRemaining = getDaysRemaining(invitation.expires_at);
    const isExpired = invitation.is_expired || (daysRemaining !== null && daysRemaining <= 0);
    const isExpiringSoon = daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 2;

    const handleCancelClick = () => setShowCancelConfirm(true);
    const handleCancelConfirm = () => {
      if (!onCancelInvitation || !permissions.canManageInvitations) return;
      onCancelInvitation(invitation.id);
      setShowCancelConfirm(false);
    };
    const handleCancelAbort = () => setShowCancelConfirm(false);
    const handleResend = () => {
      if (!onResendInvitation || !permissions.canManageInvitations) return;
      onResendInvitation(invitation.id);
    };

    return (
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        <div className="flex-1 overflow-hidden">
          <div className="h-full pt-20 pl-36 pr-36 flex flex-col">
            {/* Header */}
            <div className="flex items-start gap-6 mb-10">
              <div className="h-20 w-20 shrink-0 rounded-full bg-amber-500/20 border-2 border-dashed border-amber-500/50 flex items-center justify-center">
                <Mail className="w-10 h-10 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-4xl font-bold text-foreground mb-3 truncate">{invitation.email}</h1>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge variant="secondary" className="text-xs bg-amber-500/20 text-amber-300 border-amber-500/30">Pending Invitation</Badge>
                  <Badge variant="secondary" className="text-xs">{roleConfig.label}</Badge>
                  {isExpired && <Badge variant="secondary" className="text-xs bg-red-500/20 text-red-300 border-red-500/30">Expired</Badge>}
                  {isExpiringSoon && !isExpired && (
                    <Badge variant="secondary" className="text-xs bg-orange-500/20 text-orange-300 border-orange-500/30">
                      Expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 overflow-y-auto space-y-10 pb-20">
              {isExpired && (
                <Alert variant="destructive" className="bg-red-500/10 border-red-500/20">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <AlertDescription className="text-red-200">
                    This invitation has expired and can no longer be accepted. Consider resending it to generate a new invitation link.
                  </AlertDescription>
                </Alert>
              )}
              {isExpiringSoon && !isExpired && (
                <Alert className="bg-orange-500/10 border-orange-500/20">
                  <Clock className="h-4 w-4 text-orange-400" />
                  <AlertDescription className="text-orange-200">
                    This invitation will expire in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}. Consider resending it if needed.
                  </AlertDescription>
                </Alert>
              )}

              {/* Invitation Details */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Send className="w-5 h-5" />
                    Invitation Details
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Information about this pending team invitation</p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email Address</p>
                    <p className="font-medium">{invitation.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Invited Role</p>
                    <Badge variant="secondary" className="text-xs">{roleConfig.label}</Badge>
                  </div>
                  {invitation.invited_by && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Invited By</p>
                      <p className="font-medium">{invitation.invited_by.name}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Date Sent</p>
                    <p className="font-medium">{formatDate(invitation.invited_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Expiration Date</p>
                    <p className="font-medium">{formatDate(invitation.expires_at)}</p>
                  </div>
                  {daysRemaining !== null && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Status</p>
                      <p className={cn("font-medium", isExpired ? "text-red-400" : "text-emerald-400")}>
                        {isExpired ? 'Expired' : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
                      </p>
                    </div>
                  )}
                </div>
                {invitation.message && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Personal Message</p>
                    <div className="p-4 bg-muted/30 border rounded-lg">
                      <p className="text-sm italic">{invitation.message}</p>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Actions */}
              {permissions.canManageInvitations && (onResendInvitation || onCancelInvitation) && (
                <>
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                        <Settings className="w-5 h-5" />
                        Actions
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Manage this invitation</p>
                    </div>
                    <div className="flex gap-3">
                      {onResendInvitation && (
                        <Button onClick={handleResend} className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Resend Invitation
                        </Button>
                      )}
                      {onCancelInvitation && (
                        showCancelConfirm ? (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={handleCancelAbort} className="border-slate-500/50 text-slate-400 hover:bg-slate-500/10">
                              <X className="w-4 h-4" />
                            </Button>
                            <Button variant="destructive" size="sm" onClick={handleCancelConfirm} className="bg-red-600 hover:bg-red-700">
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="outline" onClick={handleCancelClick} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                            <X className="w-4 h-4 mr-2" />
                            Cancel Invitation
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Permissions Preview */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                    {roleConfig.label} Permissions
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    This user will have the following permissions when they accept the invitation
                  </p>
                </div>
                <div className="space-y-6">
                  {permissionCategories.map((category) => {
                    const CategoryIcon = category.icon;
                    return (
                      <div key={category.id} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                          <h4 className="font-medium text-sm">{category.title}</h4>
                        </div>
                        <div className="space-y-2 pl-6">
                          {category.permissions.map((permission) => {
                            const hasPerm = invitationPermissions[permission.key];
                            return (
                              <div key={permission.key} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-4 h-4 rounded-full flex items-center justify-center", hasPerm ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-600/50 text-slate-500")}>
                                    {hasPerm ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                                  </div>
                                  <span>{permission.label}</span>
                                </div>
                                <span className={cn("text-xs", hasPerm ? "text-emerald-400" : "text-slate-500")}>
                                  {hasPerm ? 'Granted' : 'Denied'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ================================
  // MEMBER VIEW (canonical form pattern)
  // ================================
  if (!member) return null;

  const roleConfig = getRoleConfig(roleForm.role);
  const canEditMember = permissions.canManageTeam && member.id !== currentUser?.id;
  const canChangeRole = permissions.canUpdateTeamRoles && member.id !== currentUser?.id;
  const isOwnProfile = member.id === currentUser?.id;
  const onlineStatus = getOnlineStatus(member.user?.last_seen_at);
  const memberPermissions = getRolePermissions(roleForm.role);

  // Display role config from store truth (not form) for header badge
  const headerRoleConfig = getRoleConfig(member.role);

  const navSections = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'role', label: 'Role' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'activity', label: 'Activity' }
  ];

  return (
    <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
      <div className="flex-1 overflow-hidden">
        <div className="h-full pt-20 pl-36 pr-36 flex flex-col">
          {/* Header */}
          <div className="flex items-start gap-6 mb-10">
            <Avatar className="h-20 w-20 shrink-0">
              <AvatarFallback className="text-2xl font-semibold">
                {getInitials(member.user?.name || '')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h1 className="text-4xl font-bold text-foreground mb-3 truncate">{member.user?.name}</h1>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {isOwnProfile && <Badge variant="secondary" className="text-xs">You</Badge>}
                {!member.is_active && (
                  <Badge variant="secondary" className="text-xs bg-red-500/20 text-red-300 border-red-500/30">Inactive</Badge>
                )}
                <Badge variant="secondary" className="text-xs">{headerRoleConfig.label}</Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <div className={cn("w-2 h-2 rounded-full", onlineStatus.bgColor)}></div>
                  {onlineStatus.label}
                </span>
              </div>
            </div>

            {/* Deactivate / Reactivate */}
            {permissions.canRemoveTeamMembers && canEditMember && (
              <div className="shrink-0">
                {member.is_active ? (
                  onRemoveMember && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => { if (confirm(`Deactivate ${member.user?.name}?`)) onRemoveMember(member.id); }}
                      disabled={savingMember || savingRole}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      Deactivate
                    </Button>
                  )
                ) : (
                  onReactivateMember && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => { if (confirm(`Reactivate ${member.user?.name}?`)) onReactivateMember(member.id); }}
                      disabled={savingMember || savingRole}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Activity className="w-3.5 h-3.5 mr-1.5" />
                      Reactivate
                    </Button>
                  )
                )}
              </div>
            )}
          </div>

          {/* Side Nav + Content */}
          <div className="flex-1 flex min-h-0">
            {/* Side Navigation */}
            <div className="w-40 flex-shrink-0">
              <div className="space-y-1 sticky top-0">
                {navSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "block w-full text-left text-sm py-1 transition-colors",
                      activeSection === section.id
                        ? "text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 overflow-y-auto pl-2 space-y-10 pb-20">
              {isOwnProfile && (
                <Alert className="bg-amber-500/10 border-amber-500/20">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertDescription className="text-amber-200">
                    You're viewing your own profile. Some changes may require administrator approval.
                  </AlertDescription>
                </Alert>
              )}

              {/* ====== BASIC INFO SECTION ====== */}
              <div ref={sectionRefs.basic} data-section="basic" className="scroll-mt-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <UserIcon className="w-5 h-5" />
                      Basic Information
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Update the member's profile information and contact details
                    </p>
                  </div>
                  {canEditMember && hasBasicChanges && (
                    <Button
                      onClick={handleSaveBasic}
                      disabled={savingMember}
                      size="sm"
                      className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 hover:border-zinc-500 hover:-translate-y-0.5 transition-all duration-200 font-medium"
                    >
                      {savingMember ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save
                        </>
                      )}
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                    <Input
                      id="name"
                      value={basicForm.name}
                      onChange={(e) => {
                        setBasicForm(prev => ({ ...prev, name: e.target.value }));
                        if (errors.name) setErrors(prev => ({ ...prev, name: null }));
                      }}
                      disabled={!canEditMember}
                      className={cn(
                        "bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5",
                        errors.name && "border-red-500"
                      )}
                    />
                    {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={basicForm.email}
                      onChange={(e) => {
                        setBasicForm(prev => ({ ...prev, email: e.target.value }));
                        if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                      }}
                      disabled={!canEditMember}
                      className={cn(
                        "bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5",
                        errors.email && "border-red-500"
                      )}
                    />
                    {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">Member Since</p>
                    <p className="font-medium">{getTimeSinceJoined(member.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Last Seen</p>
                    <p className="font-medium">{formatDate(member.user?.last_seen_at)}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* ====== ROLE SECTION ====== */}
              <div ref={sectionRefs.role} data-section="role" className="scroll-mt-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Lock className="w-5 h-5" />
                      Role Management
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {canChangeRole ? "Select the role that defines this member's access level and capabilities" : "Role management is restricted for this member"}
                    </p>
                  </div>
                  {canChangeRole && hasRoleChanges && (
                    <Button
                      onClick={handleSaveRole}
                      disabled={savingRole}
                      size="sm"
                      className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 hover:border-zinc-500 hover:-translate-y-0.5 transition-all duration-200 font-medium"
                    >
                      {savingRole ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {canChangeRole ? (
                  <RadioGroup value={roleForm.role} onValueChange={(value) => setRoleForm({ role: value })} className="space-y-4">
                    {roleOptions.map((option) => (
                      <Label key={option.value} htmlFor={option.value} className="flex items-start gap-3 cursor-pointer">
                        <RadioGroupItem value={option.value} id={option.value} className="mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{option.label}</div>
                          <p className="text-muted-foreground text-sm mt-0.5">{option.description}</p>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-muted/50 border rounded-lg">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Role Management Restricted</p>
                      <p className="text-muted-foreground text-sm">You don't have permission to change this member's role.</p>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* ====== PERMISSIONS SECTION (read-only) ====== */}
              <div ref={sectionRefs.permissions} data-section="permissions" className="scroll-mt-6 space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                    {roleConfig.label} Permissions
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Permissions are automatically assigned based on role
                  </p>
                </div>
                <div className="space-y-6">
                  {permissionCategories.map((category) => {
                    const CategoryIcon = category.icon;
                    return (
                      <div key={category.id} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                          <h4 className="font-medium text-sm">{category.title}</h4>
                        </div>
                        <div className="space-y-2 pl-6">
                          {category.permissions.map((permission) => {
                            const hasPerm = memberPermissions[permission.key];
                            return (
                              <div key={permission.key} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-4 h-4 rounded-full flex items-center justify-center", hasPerm ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-600/50 text-slate-500")}>
                                    {hasPerm ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                                  </div>
                                  <span>{permission.label}</span>
                                </div>
                                <span className={cn("text-xs", hasPerm ? "text-emerald-400" : "text-slate-500")}>
                                  {hasPerm ? 'Granted' : 'Denied'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* ====== ACTIVITY SECTION (read-only) ====== */}
              <div ref={sectionRefs.activity} data-section="activity" className="scroll-mt-6 space-y-5">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                    <Activity className="w-5 h-5" />
                    Activity & Status
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Track this member's activity and online status
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">Current Status</p>
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-2 rounded-full", onlineStatus.bgColor)}></div>
                      <p className={cn("font-medium", onlineStatus.color)}>{onlineStatus.label}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Last Activity</p>
                    <p className="font-medium">{formatDate(member.user?.last_seen_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberEditor;
